import styles from './MenuBanners.module.scss';

import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMerchant } from 'hooks/context/merchant';
import { useMerchantState } from 'hooks/context/MerchantState';
import { useSmartNavigate } from 'hooks/useSmartNavigate';
import { useCustomer } from 'hooks/context/customer/customer';
import { Banner, BannerVariant } from './Banner';
import { CLOSED_BUSINESS_BANNER_TEST_ID, EXPIRED_LINK_BANNER_TEST_ID } from './Banner/BannerTestIds';
import { useMenu } from './MenuContext/MenuContext';

type MenuBannersProps = React.HTMLAttributes<HTMLElement>;

export const MenuBanners: React.FC<MenuBannersProps & {children?: never}> = () => {
  const { t } = useTranslation();
  const navigate = useSmartNavigate();
  const { error: menuError, loading: isMenuLoading } = useMenu();
  const { status, merchant, isLoading: isMerchantLoading } = useMerchant();
  const { isMagicLinkExpired, customer } = useCustomer();
  const { isAcceptingOrders, isDineReadOnly, isInvalidDineIn, showGiftCardOnlyState } = useMerchantState();

  const isMenuLoaded = isMenuLoading && null;
  const showBanner = !isMerchantLoading && isMenuLoaded !== null && !isAcceptingOrders && !showGiftCardOnlyState;

  const bannerStatus = menuError ? 'fetchError' : status;
  const bannerContent = useMemo(() => {
    if (bannerStatus === 'fetchError') return bannerStatus;
    if (isDineReadOnly) return 'dineInClosed';
    if (isInvalidDineIn) return 'paused';

    return bannerStatus;
    }, [bannerStatus, isDineReadOnly, isInvalidDineIn]);

  if (showBanner) {
    return (
      <Banner variant={BannerVariant.CLOSED} className={styles.banner} data-testid={CLOSED_BUSINESS_BANNER_TEST_ID}>
        {t(`bannerMessage.${bannerContent}`, { merchantName: merchant?.name })}
      </Banner>
    );
  }

  return (
    <>
    {!customer && isMagicLinkExpired && <Banner variant={BannerVariant.ALERT} className={styles.expiredBanner} data-testid={EXPIRED_LINK_BANNER_TEST_ID}>
        <Trans i18nKey="authErrorMessage.EMAIL_CONFIRMATION_TOKEN_INVALID">
          Your magic link expired. You can <button className={styles.request} onClick={() => navigate('~login/info', { state: { redirectPath: '~menu', defaultToEmail: true } })}>request a new one.</button>
        </Trans>
      </Banner>}
    </>
  );
};
